import { useId } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

const QTY_RE = /^\d{1,9}$/;

/** Whole-number quantity per scan: 1 by default, ×N for a multi-pack. Touch-sized buttons. */
export function QtyStepper({
  value, onChange, onDone, disabled,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  /** Enter in the field: hand the focus back to the scan field. */
  readonly onDone?: () => void;
  readonly disabled?: boolean;
}): JSX.Element {
  const { t } = useLanguage();
  const id = useId();
  const n = QTY_RE.test(value) ? Number(value) : 1;
  return (
    <div className="qty-stepper" role="group" aria-labelledby={id}>
      <span id={id} className="qty-stepper-label">{t('scan.qty')}</span>
      <button type="button" aria-label={t('scan.qty.dec')} disabled={disabled || n <= 1} onClick={() => onChange(String(Math.max(1, n - 1)))}>−</button>
      <input
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={t('scan.qty')}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, 9))}
        onBlur={() => { if (!QTY_RE.test(value) || Number(value) < 1) onChange('1'); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onDone?.(); } }}
      />
      <button type="button" aria-label={t('scan.qty.inc')} disabled={disabled || n >= 999999} onClick={() => onChange(String(n + 1))}>+</button>
    </div>
  );
}

/** The quantity to send: a positive whole number, never an empty or zero field. */
export function qtyToSend(value: string): string {
  return QTY_RE.test(value) && Number(value) >= 1 ? String(Number(value)) : '1';
}
